import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { appDatePickerSlots } from "../shared/datePickerSlots";
import "dayjs/locale/en";
import type {
  TaskTableColumn,
  TaskTableDefinition,
  TaskTableField,
  TaskTableFormState,
  TaskTableRow,
} from "../../types/manualAssessmentWorkflow";
import {
  buildOverallApproveStickyRightByField,
  getEqualColumnWidthSx,
  getOverallApproveColumnWidthSx,
  getOverallApproveStickyColumnSx,
  getWideOverallApproveTableMinWidthPx,
  HOME_LIST_TABLE_BODY_ROW_SX,
  HOME_LIST_TABLE_CELL_CONTROL_SLOT_SX,
  HOME_LIST_TABLE_CELL_FIELD_SX,
  HOME_LIST_TABLE_CONTAINER_SX,
  HOME_LIST_TABLE_HEAD_ROW_SX,
  HOME_LIST_TABLE_INACTIVE_TEXT_SX,
  HOME_LIST_TABLE_SX,
  HOME_LIST_TABLE_TOKENS,
  isCompactEqualWidthApproveTable,
  ATTACHMENT_COLUMN_WIDTH_SX,
  LONG_TEXT_COLUMN_WIDTH_SX,
  OVERALL_APPROVE_ATTACHMENT_FIELD_NAMES,
  TRIAGE_ASSESSMENT_REQUIRED_COLUMN_WIDTH_SX,
  TRIAGE_INDEX_COLUMN_WIDTH_SX,
} from "../../styles/homeListTableTokens";
import type { FormData } from "../FormRenderer/types";
import { SME_ASSESSMENT_BY_CATEGORY_TABLE_NAME } from "../../utils/manualAssessmentTaskResult";
import TaskTableAsyncSelect from "./TaskTableAsyncSelect";
import TaskTableAttachmentCell from "./TaskTableAttachmentCell";
import TaskTableLongTextCell from "./TaskTableLongTextCell";
import TaskTableReviewerDisplay from "./TaskTableReviewerDisplay";
import {
  applyOverallApproveTableCellUpdate,
  applyTriageSelectAllNoImpact,
  applyTriageTableCellUpdate,
  buildTaskTableCellAsyncFormData,
  buildTaskTableCellValidationErrorKey,
  collectTriageColumnDescriptors,
  findColumnByDescriptor,
  getAssessmentRequiredTableHeader,
  getReviewerDisplayValue,
  getRowApproveValue,
  getTaskTableCellValue,
  getTaskTableFieldName,
  getTaskTableIndexDisplayValue,
  getTaskTableIndexLabel,
  getTaskTableIndexValue,
  getRowTaskInstanceId,
  getTriageAssessmentRequiredStateKey,
  getTriageAssessmentRequiredValue,
  getTriageSelectAllSwitchState,
  getMinimumTargetDateIso,
  isManualAssessmentDateField,
  parseManualAssessmentDate,
  TASK_DURATION_DATE_API_FORMAT,
  isFieldDisabled,
  isHiddenTaskTableColumn,
  isToggleField,
  coerceBooleanFieldValue,
  isTriageReviewerColumn,
  isTriageTaskTable,
  resolveAssessmentRequiredInitialValue,
  resolveColumnTaskInstanceId,
  setTaskTableCellValue,
  shouldShowApprovalCommentsInput,
  sortTriageTableDefinition,
  stripLabelRequiredAsterisk,
  tableSupportsAssessmentRequiredRows,
  type TaskTableCellValidationError,
  type TriageTableColumnDescriptor,
  APPROVAL_COMMENTS_INACTIVE_VALUE,
  TRIAGE_TABLE_SELECT_ALL_TYPOGRAPHY_SX,
} from "../../utils/manualAssessmentTaskTable";
import {
  getTaskTableAttachmentColumnLabel,
  isTaskTableAttachmentField,
  normalizeTaskTableAttachmentDownloadConfig,
} from "../../utils/taskTableAttachment";

export type ManualAssessmentTaskTableProps = Readonly<{
  tables: TaskTableDefinition[];
  state: TaskTableFormState;
  onChange: (nextState: TaskTableFormState) => void;
  readOnly?: boolean;
  /** Form values used by async select fields (e.g. taskOwners for task-assignees). */
  asyncFormData?: FormData;
  /** Inline validation errors keyed by table row/column. */
  validationErrors?: readonly TaskTableCellValidationError[];
}>;

type ColumnDescriptor = TriageTableColumnDescriptor;

const INACTIVE_CELL_PLACEHOLDER = "—";

const LONG_TEXT_FIELD_NAMES = new Set([
  "assessmentComments",
  "reviewComments",
  "approvalComments",
]);

const TABLE_CELL_ERROR_TEXT_SX = {
  color: "error.main",
  fontFamily: '"Nunito Sans", sans-serif',
  fontSize: 12,
  lineHeight: "18px",
} as const;

function isLongTextField(field: TaskTableField): boolean {
  const fieldName = getTaskTableFieldName(field);
  if (fieldName && LONG_TEXT_FIELD_NAMES.has(fieldName)) return true;
  const fieldType = String(field.type ?? "text").toLowerCase();
  return (
    fieldType === "textarea" ||
    field.textArea === true ||
    field.textarea === true
  );
}

function getFieldMaxLen(field: TaskTableField): number | undefined {
  return typeof field.maxLen === "number" && field.maxLen > 0
    ? field.maxLen
    : undefined;
}

function getFieldPlaceholder(field: TaskTableField): string | undefined {
  return typeof field.placeholder === "string" ? field.placeholder : undefined;
}

function getFieldLabel(field: TaskTableField, fallback: string): string {
  const label =
    typeof field.label === "string" ? field.label.trim() : "";
  return stripLabelRequiredAsterisk(label || fallback);
}

/** Stable triage column widths so toggling assessment_required does not reflow. */
function getTriageColumnWidthSx(
  fieldName: string | "index",
  flexColumnCount: number,
) {
  if (fieldName === "index") {
    return TRIAGE_INDEX_COLUMN_WIDTH_SX;
  }
  if (fieldName === "assessment_required") {
    return TRIAGE_ASSESSMENT_REQUIRED_COLUMN_WIDTH_SX;
  }
  if (flexColumnCount <= 0) return undefined;
  // Assign To SME Leader / Reviewer / Target Date (+ other flex cols) share the rest.
  const narrow = HOME_LIST_TABLE_TOKENS.triageNarrowColumnWidthPercent;
  const flexWidth = `${(100 - narrow * 2) / flexColumnCount}%`;
  return {
    width: flexWidth,
    maxWidth: flexWidth,
  } as const;
}

function renderCellValidationMessage(errorMessage?: string): React.ReactNode {
  if (!errorMessage) return null;
  return (
    <Typography component="span" role="alert" sx={TABLE_CELL_ERROR_TEXT_SX}>
      {errorMessage}
    </Typography>
  );
}

function renderColumnHeaderLabel(
  label: string,
  required?: boolean,
): React.ReactNode {
  const displayLabel = stripLabelRequiredAsterisk(label);
  return (
    <>
      {displayLabel}
      {required ? (
        <Box component="span" sx={{ color: "error.main" }}>
          {" *"}
        </Box>
      ) : null}
    </>
  );
}

function collectColumnDescriptors(table: TaskTableDefinition): ColumnDescriptor[] {
  const indexKey = table.index ?? "department";
  const referenceRow =
    table.rows?.reduce((best, row) =>
      (row.columns?.length ?? 0) > (best.columns?.length ?? 0) ? row : best,
    table.rows[0]);

  return (referenceRow?.columns ?? [])
    .map((column, sourceColumnIndex) => {
      const fieldName = getTaskTableFieldName(column.field);
      if (!fieldName || fieldName === indexKey) return null;
      if (isHiddenTaskTableColumn(column)) return null;
      return {
        columnKey: `${fieldName}::${sourceColumnIndex}`,
        fieldName,
        label: column.field?.label?.trim() || fieldName,
        type: String(column.field?.type ?? "text"),
        sourceColumnIndex,
        ...(column.field?.required === true ||
        fieldName === "approve" ||
        fieldName === "approvalComments"
          ? { required: true }
          : {}),
      };
    })
    .filter((descriptor): descriptor is ColumnDescriptor => Boolean(descriptor));
}

function renderAttachmentCell(
  field: TaskTableField,
  fieldName: string,
  value: unknown,
  taskInstanceUniqueId: string,
): React.ReactNode {
  const fieldRecord = field as Record<string, unknown>;
  return (
    <TaskTableAttachmentCell
      label={getTaskTableAttachmentColumnLabel(field) || getFieldLabel(field, fieldName || "Attachment")}
      value={value}
      extraState={
        taskInstanceUniqueId
          ? { taskInstanceUniqueId }
          : {}
      }
      downloadConfig={normalizeTaskTableAttachmentDownloadConfig(
        fieldRecord.downloadConfig,
      )}
      checkDownloadable={fieldRecord.checkDownloadable === true}
    />
  );
}

function renderInactiveCell(
  placeholder: string = INACTIVE_CELL_PLACEHOLDER,
): React.ReactNode {
  return (
    <Box sx={HOME_LIST_TABLE_CELL_CONTROL_SLOT_SX}>
      <Typography component="span" sx={HOME_LIST_TABLE_INACTIVE_TEXT_SX}>
        {placeholder}
      </Typography>
    </Box>
  );
}

function renderToggleSwitch(
  value: unknown,
  disabled: boolean,
  onChange: (checked: boolean) => void,
  fallback = false,
): React.ReactNode {
  return (
    <Box sx={HOME_LIST_TABLE_CELL_CONTROL_SLOT_SX}>
      <Switch
        size="small"
        checked={coerceBooleanFieldValue(value, fallback)}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
    </Box>
  );
}

function renderTableSelect(
  field: TaskTableField,
  value: unknown,
  disabled: boolean,
  asyncFormData: FormData | undefined,
  onChange: (nextValue: unknown) => void,
  errorMessage?: string,
): React.ReactNode {
  return (
    <TaskTableAsyncSelect
      field={field}
      value={value}
      disabled={disabled}
      formData={asyncFormData}
      onChange={onChange}
      error={Boolean(errorMessage)}
      helperText={errorMessage}
    />
  );
}

function renderTableDateField(
  value: unknown,
  disabled: boolean,
  onChange: (nextValue: unknown) => void,
  options?: {
    minDateIso?: string;
    errorMessage?: string;
    placeholder?: string;
  },
): React.ReactNode {
  const parsed = parseManualAssessmentDate(value);
  const minDate = options?.minDateIso
    ? parseManualAssessmentDate(options.minDateIso)
    : null;

  return (
    <DatePicker
      enableAccessibleFieldDOMStructure={false}
      format={TASK_DURATION_DATE_API_FORMAT}
      value={parsed}
      minDate={minDate ?? undefined}
      disabled={disabled}
      slots={appDatePickerSlots}
      onChange={(date) => {
        if (!date?.isValid()) {
          onChange(null);
          return;
        }
        if (minDate && date.isBefore(minDate, "day")) return;
        onChange(date.locale("en").format(TASK_DURATION_DATE_API_FORMAT));
      }}
      slotProps={{
        textField: {
          size: "small",
          fullWidth: true,
          error: Boolean(options?.errorMessage),
          helperText: options?.errorMessage,
          placeholder: options?.placeholder || TASK_DURATION_DATE_API_FORMAT,
          sx: HOME_LIST_TABLE_CELL_FIELD_SX,
        },
      }}
    />
  );
}

function renderTriageCellControl(
  table: TaskTableDefinition,
  row: TaskTableRow,
  column: TaskTableColumn | undefined,
  descriptor: ColumnDescriptor,
  state: TaskTableFormState,
  rowActive: boolean,
  readOnly: boolean,
  asyncFormData: FormData | undefined,
  onChange: (next: TaskTableFormState) => void,
  errorMessage?: string,
): React.ReactNode {
  if (!column) return renderInactiveCell();

  const field = column.field ?? ({} as TaskTableField);
  const fieldName = getTaskTableFieldName(field);
  const cellAsyncFormData = buildTaskTableCellAsyncFormData(
    asyncFormData,
    table,
    row,
    column,
  );
  const taskInstanceUniqueId = isToggleField(field)
    ? getTriageAssessmentRequiredStateKey(table, row)
    : (column.taskInstanceUniqueId ?? getRowTaskInstanceId(row));
  const value = getTaskTableCellValue(
    state,
    table.name,
    taskInstanceUniqueId,
    fieldName,
    field.fieldValue,
  );

  const updateValue = (nextValue: unknown) => {
    onChange(
      applyTriageTableCellUpdate(
        table,
        state,
        row,
        column,
        fieldName,
        nextValue,
      ),
    );
  };

  if (isToggleField(field)) {
    const toggleFallback =
      fieldName === "assessment_required"
        ? resolveAssessmentRequiredInitialValue({
            ...field,
            // Display fallback only — prefer already-resolved `value` (fieldValue/state).
            fieldValue: undefined,
          })
        : false;
    return renderToggleSwitch(
      value,
      readOnly || isFieldDisabled(field),
      updateValue,
      toggleFallback,
    );
  }

  if (!rowActive) {
    return renderInactiveCell();
  }

  if (isTriageReviewerColumn(field)) {
    const reviewerValue =
      column != null
        ? getReviewerDisplayValue(table, row, state, column)
        : value;
    return (
      <TaskTableReviewerDisplay
        table={table}
        row={row}
        value={reviewerValue}
        formData={asyncFormData}
      />
    );
  }

  const disabled = readOnly || isFieldDisabled(field);
  const fieldType = String(field.type ?? "text").toLowerCase();

  if (isTaskTableAttachmentField(field)) {
    return renderAttachmentCell(
      field,
      fieldName,
      value,
      taskInstanceUniqueId,
    );
  }

  if (fieldType === "select") {
    return renderTableSelect(
      field,
      value,
      disabled,
      cellAsyncFormData,
      updateValue,
      errorMessage,
    );
  }

  if (isManualAssessmentDateField(fieldName, field.type)) {
    return renderTableDateField(value, disabled, updateValue, {
      minDateIso: getMinimumTargetDateIso(),
      errorMessage,
      placeholder: getFieldPlaceholder(field),
    });
  }

  if (isLongTextField(field) || fieldType === "textarea") {
    return (
      <TaskTableLongTextCell
        label={getFieldLabel(field, fieldName || "Comments")}
        value={value}
        disabled={disabled}
        maxLen={getFieldMaxLen(field)}
        placeholder={getFieldPlaceholder(field)}
        errorMessage={errorMessage}
        onChange={(next) => updateValue(next)}
      />
    );
  }

  if (fieldType === "checkbox") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Checkbox
          checked={Boolean(value)}
          disabled={disabled}
          onChange={(event) => updateValue(event.target.checked)}
          size="small"
        />
        {renderCellValidationMessage(errorMessage)}
      </Box>
    );
  }

  return (
    <TextField
      size="small"
      fullWidth
      disabled={disabled}
      error={Boolean(errorMessage)}
      helperText={errorMessage}
      sx={HOME_LIST_TABLE_CELL_FIELD_SX}
      value={value == null ? "" : String(value)}
      onChange={(event) => updateValue(event.target.value)}
      inputProps={
        getFieldMaxLen(field)
          ? { maxLength: getFieldMaxLen(field) }
          : undefined
      }
    />
  );
}

function resolveTaskTableColumnWidthSx(
  fieldName: string,
  options: {
    supportsAssessmentRows: boolean;
    isOverallApproveTable: boolean;
    useEqualColumnWidths: boolean;
    equalColumnCount: number;
    triageFlexColumnCount: number;
  },
) {
  if (options.supportsAssessmentRows) {
    return getTriageColumnWidthSx(fieldName, options.triageFlexColumnCount);
  }
  if (options.useEqualColumnWidths) {
    return getEqualColumnWidthSx(options.equalColumnCount);
  }
  if (options.isOverallApproveTable) {
    return getOverallApproveColumnWidthSx(fieldName);
  }
  if (LONG_TEXT_FIELD_NAMES.has(fieldName)) {
    return LONG_TEXT_COLUMN_WIDTH_SX;
  }
  if (
    (OVERALL_APPROVE_ATTACHMENT_FIELD_NAMES as readonly string[]).includes(
      fieldName,
    )
  ) {
    return ATTACHMENT_COLUMN_WIDTH_SX;
  }
  return undefined;
}

function resolveTaskTableColumnCellSx(
  fieldName: string,
  options: {
    supportsAssessmentRows: boolean;
    isOverallApproveTable: boolean;
    useEqualColumnWidths: boolean;
    equalColumnCount: number;
    triageFlexColumnCount: number;
    isHeader?: boolean;
    stickyRightByField?: ReadonlyMap<string, number>;
    leftmostStickyField?: string | null;
  },
) {
  const stickyRight = options.stickyRightByField?.get(fieldName);
  return {
    ...resolveTaskTableColumnWidthSx(fieldName, options),
    ...(options.isOverallApproveTable &&
    !options.useEqualColumnWidths &&
    stickyRight != null
      ? getOverallApproveStickyColumnSx(fieldName, {
          isHeader: options.isHeader,
          rightPx: stickyRight,
          isLeftmostSticky: options.leftmostStickyField === fieldName,
        })
      : null),
  };
}

function tableHasApproveColumn(table: TaskTableDefinition): boolean {
  return (table.rows ?? []).some((row) =>
    (row.columns ?? []).some(
      (column) => getTaskTableFieldName(column.field) === "approve",
    ),
  );
}

function renderCellControl(
  table: TaskTableDefinition,
  row: TaskTableRow,
  column: TaskTableColumn | undefined,
  state: TaskTableFormState,
  readOnly: boolean,
  asyncFormData: FormData | undefined,
  onChange: (next: TaskTableFormState) => void,
  errorMessage?: string,
): React.ReactNode {
  if (!column) return renderInactiveCell();

  const field = column.field ?? ({} as TaskTableField);
  const fieldName = getTaskTableFieldName(field);
  const cellAsyncFormData = buildTaskTableCellAsyncFormData(
    asyncFormData,
    table,
    row,
    column,
  );
  const taskInstanceUniqueId =
    column.taskInstanceUniqueId ?? getRowTaskInstanceId(row);
  const value = getTaskTableCellValue(
    state,
    table.name,
    taskInstanceUniqueId,
    fieldName,
    field.fieldValue,
  );

  const updateValue = (nextValue: unknown) => {
    onChange(
      fieldName === "approve"
        ? applyOverallApproveTableCellUpdate(
            table,
            state,
            row,
            column,
            fieldName,
            nextValue,
          )
        : setTaskTableCellValue(
            state,
            table.name,
            taskInstanceUniqueId,
            fieldName,
            nextValue,
          ),
    );
  };

  if (fieldName === "approvalComments") {
    const approveValue = getRowApproveValue(table, row, state);
    if (!shouldShowApprovalCommentsInput(field, approveValue)) {
      return renderInactiveCell(APPROVAL_COMMENTS_INACTIVE_VALUE);
    }
  }

  // Reject Comments: backend often sends readonly/disabled; still editable when Reject.
  const disabled =
    fieldName === "approvalComments"
      ? Boolean(readOnly)
      : readOnly || isFieldDisabled(field);

  const fieldType = String(field.type ?? "text").toLowerCase();
  const useTextArea =
    fieldType === "textarea" || field.textArea === true;
  const isLongTextCell = isLongTextField(field);

  if (isTaskTableAttachmentField(field)) {
    return renderAttachmentCell(
      field,
      fieldName,
      value,
      taskInstanceUniqueId,
    );
  }

  if (fieldType === "toggle" || isToggleField(field)) {
    return renderToggleSwitch(
      value,
      disabled,
      updateValue,
    );
  }

  if (fieldType === "checkbox") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Checkbox
          checked={Boolean(value)}
          disabled={disabled}
          onChange={(event) => updateValue(event.target.checked)}
          size="small"
        />
        {renderCellValidationMessage(errorMessage)}
      </Box>
    );
  }

  if (fieldType === "select") {
    return renderTableSelect(
      field,
      value,
      disabled,
      cellAsyncFormData,
      updateValue,
      errorMessage,
    );
  }

  if (isManualAssessmentDateField(fieldName, field.type)) {
    return renderTableDateField(value, disabled, updateValue, {
      minDateIso: getMinimumTargetDateIso(),
      errorMessage,
      placeholder: getFieldPlaceholder(field),
    });
  }

  if (useTextArea || isLongTextCell) {
    return (
      <TaskTableLongTextCell
        label={getFieldLabel(field, fieldName || "Comments")}
        value={value}
        disabled={disabled}
        maxLen={getFieldMaxLen(field)}
        placeholder={getFieldPlaceholder(field)}
        errorMessage={errorMessage}
        onChange={(next) => updateValue(next)}
      />
    );
  }

  return (
    <TextField
      size="small"
      fullWidth
      disabled={disabled}
      error={Boolean(errorMessage)}
      helperText={errorMessage}
      sx={HOME_LIST_TABLE_CELL_FIELD_SX}
      placeholder={getFieldPlaceholder(field)}
      value={value == null ? "" : String(value)}
      onChange={(event) => {
        const next = event.target.value;
        const limit = getFieldMaxLen(field);
        updateValue(
          limit != null && next.length > limit ? next.slice(0, limit) : next,
        );
      }}
      inputProps={
        getFieldMaxLen(field)
          ? { maxLength: getFieldMaxLen(field) }
          : undefined
      }
    />
  );
}

function getTriageCellValidationKey(
  table: TaskTableDefinition,
  row: TaskTableRow,
  column: TaskTableColumn,
  fieldName: string,
): string {
  const taskInstanceUniqueId = isToggleField(column.field)
    ? getTriageAssessmentRequiredStateKey(table, row)
    : resolveColumnTaskInstanceId(row, column);
  return buildTaskTableCellValidationErrorKey(
    table.name,
    taskInstanceUniqueId,
    fieldName,
  );
}

function TaskTableBlock({
  table,
  state,
  onChange,
  readOnly,
  asyncFormData,
  validationErrors,
  hasSmeAssessmentByCategoryTable,
}: Readonly<{
  table: TaskTableDefinition;
  state: TaskTableFormState;
  onChange: (nextState: TaskTableFormState) => void;
  readOnly?: boolean;
  asyncFormData?: FormData;
  validationErrors?: readonly TaskTableCellValidationError[];
  hasSmeAssessmentByCategoryTable: boolean;
}>) {
  const isTriage = isTriageTaskTable(table);
  const supportsAssessmentRows = tableSupportsAssessmentRequiredRows(table);
  const isOverallApproveTable = tableHasApproveColumn(table);
  const displayTable = useMemo(
    () => (isTriage ? sortTriageTableDefinition(table) : table),
    [isTriage, table],
  );
  const indexLabel = getTaskTableIndexLabel(displayTable);
  const columnDescriptors = useMemo(
    () =>
      isTriage
        ? collectTriageColumnDescriptors(displayTable)
        : collectColumnDescriptors(displayTable),
    [isTriage, displayTable],
  );
  const assessmentHeader = useMemo(
    () =>
      supportsAssessmentRows
        ? getAssessmentRequiredTableHeader(displayTable, {
            hasSmeAssessmentByCategoryTable,
          })
        : null,
    [supportsAssessmentRows, displayTable, hasSmeAssessmentByCategoryTable],
  );
  const selectAllSwitchState = useMemo(
    () =>
      supportsAssessmentRows && assessmentHeader?.showSelectAllSwitch
        ? getTriageSelectAllSwitchState(displayTable, state)
        : null,
    [
      supportsAssessmentRows,
      assessmentHeader?.showSelectAllSwitch,
      displayTable,
      state,
    ],
  );
  const validationErrorMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const error of validationErrors ?? []) {
      if (error.tableName !== table.name) continue;
      map.set(
        buildTaskTableCellValidationErrorKey(
          error.tableName,
          error.taskInstanceUniqueId,
          error.fieldName,
        ),
        error.message,
      );
    }
    return map;
  }, [table.name, validationErrors]);
  const triageFlexColumnCount = useMemo(
    () =>
      supportsAssessmentRows
        ? columnDescriptors.filter(
            (column) => column.fieldName !== "assessment_required",
          ).length
        : 0,
    [supportsAssessmentRows, columnDescriptors],
  );
  const columnFieldNames = useMemo(
    () => columnDescriptors.map((column) => column.fieldName),
    [columnDescriptors],
  );
  const useEqualColumnWidths = isCompactEqualWidthApproveTable(
    isOverallApproveTable,
    columnFieldNames,
  );
  /** Index + data columns — used when splitting widths evenly. */
  const equalColumnCount = columnDescriptors.length + 1;
  const stickyRightByField = useMemo(() => {
    if (!isOverallApproveTable || useEqualColumnWidths) {
      return new Map<string, number>();
    }
    return buildOverallApproveStickyRightByField(columnFieldNames);
  }, [isOverallApproveTable, useEqualColumnWidths, columnFieldNames]);
  const leftmostStickyField = useMemo(() => {
    for (const column of columnDescriptors) {
      if (stickyRightByField.has(column.fieldName)) {
        return column.fieldName;
      }
    }
    return null;
  }, [columnDescriptors, stickyRightByField]);

  if (!displayTable.rows?.length) return null;

  const columnWidthOptions = {
    supportsAssessmentRows,
    isOverallApproveTable,
    useEqualColumnWidths,
    equalColumnCount,
    triageFlexColumnCount,
    stickyRightByField,
    leftmostStickyField,
  };
  const useFixedTableLayout =
    supportsAssessmentRows || isOverallApproveTable || useEqualColumnWidths;
  const useWideApproveScrollLayout =
    isOverallApproveTable && !useEqualColumnWidths;

  let tableTitleBlock: React.ReactNode = null;
  if (supportsAssessmentRows && assessmentHeader) {
    tableTitleBlock = (
      <Box sx={{ mb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography sx={TRIAGE_TABLE_SELECT_ALL_TYPOGRAPHY_SX}>
            {assessmentHeader.title}
          </Typography>
          {assessmentHeader.showSelectAllSwitch ? (
            <Switch
              size="small"
              sx={{ ml: "4px" }}
              checked={selectAllSwitchState?.checked ?? false}
              disabled={readOnly}
              onChange={(event) =>
                onChange(
                  applyTriageSelectAllNoImpact(
                    displayTable,
                    state,
                    event.target.checked,
                  ),
                )
              }
            />
          ) : null}
        </Box>
        {assessmentHeader.description ? (
          <Typography
            component="p"
            sx={{
              mt: 0.75,
              mb: 0,
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 400,
              fontSize: 12,
              lineHeight: "18px",
              color: "#8C8C8C",
              maxWidth: 960,
            }}
          >
            {assessmentHeader.description}
          </Typography>
        ) : null}
      </Box>
    );
  } else if (displayTable.name) {
    tableTitleBlock = (
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
        {displayTable.name.replace(/_/g, " ")}
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      {tableTitleBlock}
      <TableContainer
        sx={{
          ...HOME_LIST_TABLE_CONTAINER_SX,
          ...(useWideApproveScrollLayout
            ? { overflowX: "auto", overflowY: "hidden" }
            : null),
        }}
      >
        <Table
          size="small"
          sx={{
            ...HOME_LIST_TABLE_SX,
            ...(useFixedTableLayout ? { tableLayout: "fixed" } : null),
            ...(useWideApproveScrollLayout
              ? {
                  minWidth: getWideOverallApproveTableMinWidthPx(columnFieldNames),
                }
              : null),
          }}
        >
          <TableHead>
            <TableRow sx={HOME_LIST_TABLE_HEAD_ROW_SX}>
              <TableCell
                sx={resolveTaskTableColumnCellSx("index", {
                  ...columnWidthOptions,
                  isHeader: true,
                })}
              >
                {indexLabel}
              </TableCell>
              {columnDescriptors.map((column) => (
                <TableCell
                  key={column.columnKey}
                  sx={resolveTaskTableColumnCellSx(column.fieldName, {
                    ...columnWidthOptions,
                    isHeader: true,
                  })}
                  data-sticky-field={
                    stickyRightByField.has(column.fieldName)
                      ? column.fieldName
                      : undefined
                  }
                  data-sticky-right={
                    stickyRightByField.has(column.fieldName)
                      ? String(stickyRightByField.get(column.fieldName))
                      : undefined
                  }
                >
                  {renderColumnHeaderLabel(
                    column.fieldName === "taskAttachment" ||
                      column.fieldName === "attachment"
                      ? getTaskTableAttachmentColumnLabel({
                          field: column.fieldName,
                          label: column.label,
                        })
                      : column.label,
                    column.required,
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {displayTable.rows.map((row) => {
              const rowKey = getTaskTableIndexValue(displayTable, row);
              const rowDisplayValue = getTaskTableIndexDisplayValue(
                displayTable,
                row,
              );
              const rowActive = supportsAssessmentRows
                ? getTriageAssessmentRequiredValue(displayTable, state, row)
                : true;
              return (
                <TableRow
                  key={rowKey || JSON.stringify(row.columns)}
                  sx={HOME_LIST_TABLE_BODY_ROW_SX}
                >
                  <TableCell
                    sx={{
                      fontWeight: HOME_LIST_TABLE_TOKENS.cellFontWeight,
                      color: HOME_LIST_TABLE_TOKENS.cellColor,
                      ...resolveTaskTableColumnCellSx("index", columnWidthOptions),
                    }}
                  >
                    {rowDisplayValue || rowKey || INACTIVE_CELL_PLACEHOLDER}
                  </TableCell>
                  {columnDescriptors.map((descriptor) => {
                    const column = findColumnByDescriptor(row, descriptor);
                    const cellError = column
                      ? validationErrorMap.get(
                          supportsAssessmentRows
                            ? getTriageCellValidationKey(
                                displayTable,
                                row,
                                column,
                                descriptor.fieldName,
                              )
                            : buildTaskTableCellValidationErrorKey(
                                displayTable.name,
                                column.taskInstanceUniqueId ??
                                  getRowTaskInstanceId(row),
                                descriptor.fieldName,
                              ),
                        )
                      : undefined;

                    return (
                      <TableCell
                        key={`${rowKey}-${descriptor.columnKey}`}
                        sx={resolveTaskTableColumnCellSx(
                          descriptor.fieldName,
                          columnWidthOptions,
                        )}
                        data-sticky-field={
                          stickyRightByField.has(descriptor.fieldName)
                            ? descriptor.fieldName
                            : undefined
                        }
                        data-sticky-right={
                          stickyRightByField.has(descriptor.fieldName)
                            ? String(
                                stickyRightByField.get(descriptor.fieldName),
                              )
                            : undefined
                        }
                      >
                        {supportsAssessmentRows
                          ? renderTriageCellControl(
                              displayTable,
                              row,
                              column,
                              descriptor,
                              state,
                              rowActive,
                              Boolean(readOnly),
                              asyncFormData,
                              onChange,
                              cellError,
                            )
                          : renderCellControl(
                              displayTable,
                              row,
                              column,
                              state,
                              Boolean(readOnly),
                              asyncFormData,
                              onChange,
                              cellError,
                            )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export function ManualAssessmentTaskTable({
  tables,
  state,
  onChange,
  readOnly = false,
  asyncFormData,
  validationErrors,
}: ManualAssessmentTaskTableProps) {
  if (!tables.length) return null;

  const hasSmeAssessmentByCategoryTable = tables.some(
    (table) => table.name === SME_ASSESSMENT_BY_CATEGORY_TABLE_NAME,
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
      <Box>
        {tables.map((table) => (
          <TaskTableBlock
            key={table.name}
            table={table}
            state={state}
            onChange={onChange}
            readOnly={readOnly}
            asyncFormData={asyncFormData}
            validationErrors={validationErrors}
            hasSmeAssessmentByCategoryTable={hasSmeAssessmentByCategoryTable}
          />
        ))}
      </Box>
    </LocalizationProvider>
  );
}

export default ManualAssessmentTaskTable;
